import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';
import { PageContainer } from '@/components/common/page-container';
import { PageHeader } from '@/components/common/page-header';
import { Button } from '@/components/ui/button';
import { AiUploadZone } from '@/features/mercado/components/ai-upload-zone';
import { ListingForm } from '@/features/mercado/components/listing-form';
import { useCreateListing, useAnalyzePhotos } from '@/features/mercado/api';
import { useCondoContext } from '@/features/auth/use-condo-context';
import type { AiAnalysisResponse } from '@condo/contracts';

type UploadPhase = 'idle' | 'uploading' | 'analyzing' | 'complete' | 'error';

interface PhotoItem {
  id: string;
  url: string;
  name?: string;
}

export function PublicarRoute() {
  const navigate = useNavigate();
  const { condoId } = useCondoContext();
  const createListing = useCreateListing();
  const analyzePhotos = useAnalyzePhotos();

  const [phase, setPhase] = React.useState<UploadPhase>('idle');
  const [previews, setPreviews] = React.useState<string[]>([]);
  const [photos, setPhotos] = React.useState<PhotoItem[]>([]);
  const [analysis, setAnalysis] = React.useState<AiAnalysisResponse | undefined>();
  const [errorMessage, setErrorMessage] = React.useState<string>();
  const [showForm, setShowForm] = React.useState(false);

  async function handleFilesSelected(files: File[]) {
    // Create preview URLs
    const newPreviews = files.map((f) => URL.createObjectURL(f));
    setPreviews((prev) => [...prev, ...newPreviews]);

    const newPhotos: PhotoItem[] = files.map((f, i) => ({
      id: `photo-${Date.now()}-${i}`,
      url: newPreviews[i]!,
      name: f.name,
    }));
    setPhotos((prev) => [...prev, ...newPhotos]);

    // Simulate upload
    setPhase('uploading');
    await new Promise((r) => setTimeout(r, 800));

    // Call AI analysis
    setPhase('analyzing');
    try {
      const result = await analyzePhotos.mutateAsync(
        files.map((f) => `marketplace/uploads/${f.name}`),
      );
      setAnalysis(result);
      setPhase('complete');
      // Show form after a short delay for the complete animation
      setTimeout(() => setShowForm(true), 600);
    } catch {
      setPhase('error');
      setErrorMessage('No se pudo analizar las imagenes. Intenta de nuevo.');
    }
  }

  function handleRemovePhoto(index: number) {
    setPreviews((prev) => prev.filter((_, i) => i !== index));
    setPhotos((prev) => prev.filter((_, i) => i !== index));
    if (previews.length <= 1) {
      setPhase('idle');
      setShowForm(false);
      setAnalysis(undefined);
    }
  }

  function handleRetry() {
    setPhase('idle');
    setErrorMessage(undefined);
  }

  async function handleSubmit(values: {
    title: string;
    description: string;
    category: string;
    condition: string;
    price: number;
  }) {
    try {
      await createListing.mutateAsync({
        title: values.title,
        description: values.description,
        category: values.category as CreateListingCategory,
        condition: values.condition as CreateListingCondition,
        priceCLP: values.price,
        photoKeys: photos.map((p) => `marketplace/uploads/${p.id}.jpg`),
        aiGenerated: !!analysis,
      });
      toast.success('Aviso publicado exitosamente');
      navigate(`/app/condominios/${condoId}/mercado`);
    } catch {
      toast.error('No se pudo publicar el aviso');
    }
  }

  return (
    <PageContainer>
      <PageHeader
        eyebrow="Comunidad · Mercado Vecinal"
        title="Publicar aviso"
        subtitle="Sube fotos y la IA completa la informacion."
        action={
          <Button
            variant="ghost"
            onClick={() => navigate(`/app/condominios/${condoId}/mercado`)}
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
        }
      />

      <div className="mx-auto max-w-2xl">

      {/* AI Upload Zone */}
      <AiUploadZone
        phase={phase}
        previews={previews}
        analysis={
          analysis
            ? {
                suggestedTitle: analysis.title,
                suggestedDescription: analysis.description,
                suggestedCategory: analysis.category,
                suggestedCondition: analysis.condition,
                suggestedPrice: analysis.suggestedPriceCLP,
                confidence: analysis.confidence,
              }
            : undefined
        }
        errorMessage={errorMessage}
        maxPhotos={5}
        onFilesSelected={handleFilesSelected}
        onRemovePhoto={handleRemovePhoto}
        onRetry={handleRetry}
      />

      {/* Listing Form — appears after AI analysis */}
      {showForm && (
        <div className="stagger-fade mt-8">
          <ListingForm
            defaultValues={{
              title: analysis?.title ?? '',
              description: analysis?.description ?? '',
              category: (analysis?.category ?? 'otros') as ListingFormCategory,
              condition: (analysis?.condition ?? 'buen_estado') as ListingFormCondition,
              price: analysis?.suggestedPriceCLP ?? 0,
            }}
            photos={photos}
            aiSuggestions={{
              title: !!analysis?.title,
              description: !!analysis?.description,
              category: !!analysis?.category,
              condition: !!analysis?.condition,
              price: !!analysis?.suggestedPriceCLP,
            }}
            isSubmitting={createListing.isPending}
            onDeletePhoto={(id) => {
              const idx = photos.findIndex((p) => p.id === id);
              if (idx !== -1) handleRemovePhoto(idx);
            }}
            onSubmit={handleSubmit}
            onCancel={() => navigate(`/app/condominios/${condoId}/mercado`)}
          />
        </div>
      )}
      </div>
    </PageContainer>
  );
}

// Type aliases to keep the component clean
type CreateListingCategory = Parameters<
  ReturnType<typeof useCreateListing>['mutateAsync']
>[0]['category'];
type CreateListingCondition = Parameters<
  ReturnType<typeof useCreateListing>['mutateAsync']
>[0]['condition'];
type ListingFormCategory = NonNullable<
  React.ComponentProps<typeof ListingForm>['defaultValues']
>['category'];
type ListingFormCondition = NonNullable<
  React.ComponentProps<typeof ListingForm>['defaultValues']
>['condition'];
